/**
 * Knack logo marquee — fill-and-loop, seamless scroll.
 *
 * Blocks render once into .knack-logo-marquee__group. A single group is often narrower than the
 * viewport (e.g. only a handful of logos configured), so a naive two-copy -50% loop leaves the
 * right side of the viewport empty for the whole cycle. Instead we repeat the original group
 * enough times that the track spans at least one viewport width PLUS one more group, then scroll
 * by exactly one group's width (--marquee-distance). The copies line up, so there is no seam and
 * the track always fills the viewport regardless of logo count.
 *
 * Only the first (server-rendered) group stays in the accessible tree; every repeat is
 * aria-hidden. No-ops for the static layout and under prefers-reduced-motion. Re-runs on
 * shopify:section:load and (debounced) on resize so the fill stays correct as the viewport or
 * logo count changes.
 */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const build = (marquee) => {
    const viewport = marquee.querySelector('.knack-logo-marquee__viewport');
    const track = marquee.querySelector('.knack-logo-marquee__track');
    if (!viewport || !track) return;

    // Remember the original single group so every rebuild starts from a clean slate.
    if (track.dataset.knackBase == null) {
      track.dataset.knackBase = track.innerHTML;
    }
    track.innerHTML = track.dataset.knackBase;
    track.style.removeProperty('--marquee-distance');

    const group = track.querySelector('.knack-logo-marquee__group');
    // Reduced motion, or nothing to show: leave the single static group, no animation.
    if (REDUCED.matches || !group || !group.children.length) return;

    // Measure one group against the viewport. getBoundingClientRect (not scrollWidth) for
    // subpixel accuracy -- logo images can render at fractional widths, and a rounding drift
    // compounded across many copies is exactly the kind of thing that leaves a hairline gap at
    // the loop seam. Shopify's image_url/image_tag output carries explicit width/height
    // attributes at render time, so this is accurate immediately -- no need to wait for image
    // bytes to load (unlike the sibling's document.fonts.ready, which exists because webfont
    // swap changes glyph metrics after paint; images have no equivalent post-paint invalidation).
    const groupWidth = group.getBoundingClientRect().width;
    const viewWidth = viewport.clientWidth;
    if (groupWidth === 0 || viewWidth === 0) return;

    // Repeat until the track is at least one screen + one group wide, so it never empties
    // mid-loop. Unbounded on purpose, matching the sibling's shipped precedent -- worst case
    // (one tiny logo on an ultrawide monitor) is a few dozen small DOM nodes, not a real cost.
    const copies = Math.ceil(viewWidth / groupWidth) + 1;
    const base = track.dataset.knackBase;
    for (let i = 1; i < copies; i += 1) {
      const holder = document.createElement('template');
      holder.innerHTML = base;
      // The repeats are decorative duplicates; hide them from assistive tech.
      Array.from(holder.content.children).forEach((child) => child.setAttribute('aria-hidden', 'true'));
      // Strip ids from every repeat so they stay unique to the real, editable copy.
      holder.content.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      track.appendChild(holder.content);
    }

    // Loop by exactly one group width -- copy N+1 lands where copy N started, so the seam is
    // invisible.
    track.style.setProperty('--marquee-distance', `${groupWidth}px`);
  };

  const init = (root = document) => {
    root.querySelectorAll('.knack-logo-marquee--scroll').forEach(build);
  };

  // Defer the first measurement past the browser's initial layout pass. Confirmed live: calling
  // init() synchronously at module-script execution time can read viewport.clientWidth before
  // layout has settled to its real value (observed reading far smaller than the eventual settled
  // width, under-filling the track), even though the exact same measurement taken moments later is
  // correct. Double rAF (not single) guarantees at least one full layout+paint cycle has completed
  // before we measure -- the standard technique for "read real layout, not pre-layout DOM state."
  requestAnimationFrame(() => requestAnimationFrame(() => init()));

  // Theme editor: rebuild when this (or any) section re-renders.
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target || document);
  });

  // Re-fill if the viewport width changes (rotation / resize), debounced.
  let resizeTimer;
  window.addEventListener(
    'resize',
    () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => init(), 200);
    },
    { passive: true }
  );

  // Rebuild if the motion preference changes at runtime.
  const onMotionChange = () => init();
  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', onMotionChange);
  } else if (REDUCED.addListener) {
    REDUCED.addListener(onMotionChange);
  }
})();
